// resources/js/admin/services/pageService.ts

import axios from "axios";
import { Page, PageFormData, PageDuplicateData } from "../types/features/Page";
import { Revision } from "../types/features/Revision";
import { MAIN_API_URL } from "../config/api";
import { PaginatedResponse } from "../types/core/PaginatedResponse";

export const pageService = {
  // 🔓 PUBLIC - Pas de token nécessaire
  async getBySlug(siteSlug: string, slug: string, locale?: string): Promise<Page> {
    try {
      const response = await axios.get(`${MAIN_API_URL}/pages/slug/${slug}`, {
        params: { site_slug: siteSlug, locale },
      });
      return response.data;
    } catch (error) {
      console.error("Erreur lors du chargement de la page", error);
      throw error;
    }
  },

  // 🆕 PUBLIC - Récupérer une page pour le rendu public
  async getPublicPage(siteSlug: string, slug: string, locale?: string): Promise<Page> {
    try {
      const response = await axios.get(`${MAIN_API_URL}/pages/public-site/${slug}`, {
        params: { site_slug: siteSlug, locale },
      });
      return response.data;
    } catch (error) {
      console.error("Erreur lors du chargement de la page publique", error);
      throw error;
    }
  },

  // 🔒 PROTÉGÉ - Token requis
  async getAll(
    siteSlug: string,
    token: string,
    page: number = 1,
    perPage: number = 20,
    status?: string,
    search?: string
  ): Promise<PaginatedResponse<Page>> {
    try {
      const response = await axios.get(`${MAIN_API_URL}/pages`, {
        params: { site_slug: siteSlug, page, per_page: perPage, status, search },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error("Erreur lors du chargement des pages", error);
      throw error;
    }
  },

  // 🔒 PROTÉGÉ - Token requis
  async getById(token: string, id: string): Promise<Page> {
    try {
      const response = await axios.get(`${MAIN_API_URL}/pages/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`, 
        },
      });
      return response.data;
    } catch (error) {
      console.error("Erreur lors du chargement de la page", error);
      throw error;
    }
  },

  // 🔒 PROTÉGÉ - Token requis
  async create(token: string, data: PageFormData): Promise<Page> {
    try {
      const response = await axios.post(`${MAIN_API_URL}/pages`, data, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la création de la page", error);
      throw error;
    }
  },

  // 🔒 PROTÉGÉ - Token requis
  async update(token: string, id: string, data: Partial<PageFormData>): Promise<Page> {
    try {
      const response = await axios.put(`${MAIN_API_URL}/pages/${id}`, data, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la mise à jour de la page", error);
      throw error;
    }
  },

  // 🔒 PROTÉGÉ - Token requis - Mettre à jour seulement le contenu
  async updateContent(token: string, id: string, content: Record<string, any>, changeSummary?: string): Promise<Page> {
    try {
      const response = await axios.put(`${MAIN_API_URL}/pages/${id}/content`, {
        content,
        change_summary: changeSummary,
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la mise à jour du contenu", error);
      throw error;
    }
  },

  // 🔒 PROTÉGÉ - Token requis
  async delete(token: string, id: string): Promise<void> {
    try {
      await axios.delete(`${MAIN_API_URL}/pages/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
    } catch (error) {
      console.error("Erreur lors de la suppression de la page", error);
      throw error;
    }
  },

  // 🔒 PROTÉGÉ - Token requis
  async restore(token: string, id: string): Promise<Page> {
    try {
      const response = await axios.post(`${MAIN_API_URL}/pages/${id}/restore`, {}, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la restauration de la page", error);
      throw error;
    }
  },

  // 🔒 PROTÉGÉ - Token requis
  async togglePublish(token: string, id: string): Promise<Page> {
    try {
      const response = await axios.post(`${MAIN_API_URL}/pages/${id}/toggle-publish`, {}, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error("Erreur lors du changement de statut de publication", error);
      throw error;
    }
  },

  // 🔒 PROTÉGÉ - Token requis
  async duplicate(token: string, id: string, data: PageDuplicateData): Promise<Page> {
    try {
      const response = await axios.post(`${MAIN_API_URL}/pages/${id}/duplicate`, data, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la duplication de la page", error);
      throw error;
    }
  },

  // 🔒 PROTÉGÉ - Token requis
  async getRevisions(
    token: string,
    pageId: string,
    page: number = 1,
    perPage: number = 20
  ): Promise<PaginatedResponse<Revision>> {
    try {
      const response = await axios.get(`${MAIN_API_URL}/pages/${pageId}/revisions`, {
        params: { page, per_page: perPage },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error("Erreur lors du chargement des révisions", error);
      throw error;
    }
  },

  // 🔒 PROTÉGÉ - Token requis
  async restoreRevision(token: string, pageId: string, revisionId: string): Promise<Page> {
    try {
      const response = await axios.post(`${MAIN_API_URL}/pages/${pageId}/revisions/${revisionId}/restore`, {}, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error("Erreur lors de la restauration de la révision", error);
      throw error;
    }
  },
};